import { LineChart, lineElementClasses } from "@mui/x-charts";
import GaugeChart from "react-gauge-chart";
import { useState, useEffect } from "react";
import { SensorDataProps } from "../../controller/tankController";
import "./styles.css";

declare module "react-gauge-chart";

interface VolumeChartProps {
  sensorData?: SensorDataProps[];
  showLargeGraph?: boolean;
  closeLargeGraph?: () => void;
  setShowLargeGraph: (value: boolean) => void;
  timeRange?: "7days" | "24hours";
  onTimeRangeChange?: (range: "7days" | "24hours") => void;
}

function VolumeChart({
  setShowLargeGraph,
  sensorData = [],
  showLargeGraph,
  closeLargeGraph,
  timeRange = "7days",
  onTimeRangeChange,
}: VolumeChartProps) {
  const [volumeData, setVolumeData] = useState<number[]>([]);
  const [xLabels, setXLabels] = useState<string[]>([]);
  const [actualDates, setActualDates] = useState<Date[]>([]);
  const [latestVolume, setLatestVolume] = useState<string>("0.0");
  const [volumePercentage, setVolumePercentage] = useState(0);

  const TANK_HEIGHT = 110.3;
  const RADIUS = 38.3;
  const MAX_VOLUME = (Math.PI * Math.pow(RADIUS, 2) * TANK_HEIGHT) / 1000;

  const calculateVolume = (levelPercentage: number): number => {
    const currentHeight = (levelPercentage / 100) * TANK_HEIGHT;
    return parseFloat(
      ((Math.PI * Math.pow(RADIUS, 2) * currentHeight) / 1000).toFixed(1)
    );
  };

  const process7DayData = (sortedData: SensorDataProps[]) => {
    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    const last7DaysData = sortedData.filter(
      (item) => new Date(item.createdAt!) >= sevenDaysAgo
    );

    const dailyData: Record<string, SensorDataProps> = {};
    last7DaysData.forEach((item) => {
      const date = new Date(item.createdAt!).toLocaleDateString();
      dailyData[date] = item;
    });

    const volumeResults: number[] = [];
    const labels: string[] = [];
    const dates: Date[] = [];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toLocaleDateString();

      labels.push(days[date.getDay()]);
      dates.push(new Date(date));

      if (dailyData[dateString]) {
        const tankLevel = Number(dailyData[dateString].tankLevel);
        volumeResults.push(calculateVolume(tankLevel));
      } else {
        volumeResults.push(0);
      }
    }

    return { data: volumeResults, labels, dates };
  };
  const margin = { top: 10, right: 10, bottom: 10, left: 10 };

  const process24HourData = (sortedData: SensorDataProps[]) => {
    const now = new Date();
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(now.getHours() - 24);

    const last24HoursData = sortedData.filter(
      (item) => new Date(item.createdAt!) >= twentyFourHoursAgo
    );

    const hourlyData: Record<string, SensorDataProps> = {};

    last24HoursData.forEach((item) => {
      const date = new Date(item.createdAt!);
      const hourKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

      if (
        !hourlyData[hourKey] ||
        new Date(item.createdAt!) > new Date(hourlyData[hourKey].createdAt!)
      ) {
        hourlyData[hourKey] = item;
      }
    });

    const volumeResults: number[] = [];
    const labels: string[] = [];
    const dates: Date[] = [];

    for (let i = 23; i >= 0; i--) {
      const date = new Date();
      date.setHours(date.getHours() - i);
      const hourKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

      const hourLabel = date.getHours().toString().padStart(2, "0") + ":00";
      labels.push(hourLabel);
      dates.push(new Date(date));

      if (hourlyData[hourKey]) {
        const tankLevel = Number(hourlyData[hourKey].tankLevel);
        volumeResults.push(calculateVolume(tankLevel));
      } else {
        volumeResults.push(0);
      }
    }

    return { data: volumeResults, labels, dates };
  };

  useEffect(() => {
    if (sensorData.length > 0) {
      const sortedData = [...sensorData].sort(
        (a, b) =>
          new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime()
      );

      let processedResult;

      if (timeRange === "24hours") {
        processedResult = process24HourData(sortedData);
      } else {
        processedResult = process7DayData(sortedData);
      }

      setVolumeData(processedResult.data);
      setXLabels(processedResult.labels);
      setActualDates(processedResult.dates);

      if (sortedData.length > 0) {
        const latestData = sortedData[sortedData.length - 1];
        const latestLevel = Number(latestData.tankLevel);
        const volume = calculateVolume(latestLevel);
        setLatestVolume(volume.toFixed(1));

        const percentage = volume / MAX_VOLUME;
        setVolumePercentage(Math.min(Math.max(percentage, 0), 1));
      }
    }
  }, [sensorData, timeRange]);

  const getChartTitle = () => {
    const timeRangeText = timeRange === "24hours" ? "24 Hours" : "7 Days";
    return `Diesel Volume History - Last ${timeRangeText} (Litres)`;
  };

  const chartLabels =
    xLabels.length > 0
      ? xLabels
      : timeRange === "24hours"
      ? Array.from(
          { length: 24 },
          (_, i) => `${i.toString().padStart(2, "0")}:00`
        )
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const largeMargin = { top: 20, right: 30, bottom: 30, left: 40 };

  const formatDateForTooltip = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateTimeForTooltip = (date: Date) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const dateStr = date.toLocaleDateString("en-US", dateOptions);
    const timeStr = date.toLocaleTimeString("en-US", timeOptions);
    return `${dateStr} at ${timeStr}`;
  };

  return (
    <div className="chart-container">
      {/* Time Range Selector */}
      {onTimeRangeChange && (
        <div className="time-range-selector" style={{ marginBottom: "10px" }}>
          <button
            className={`time-range-button ${
              timeRange === "7days" ? "active" : ""
            }`}
            onClick={() => onTimeRangeChange("7days")}
            style={{
              padding: "5px 10px",
              marginRight: "5px",
              backgroundColor: timeRange === "7days" ? "#4285F4" : "#f0f0f0",
              color: timeRange === "7days" ? "white" : "black",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            7 Days
          </button>
          <button
            className={`time-range-button ${
              timeRange === "24hours" ? "active" : ""
            }`}
            onClick={() => onTimeRangeChange("24hours")}
            style={{
              padding: "5px 10px",
              backgroundColor: timeRange === "24hours" ? "#4285F4" : "#f0f0f0",
              color: timeRange === "24hours" ? "white" : "black",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            24 Hours
          </button>
        </div>
      )}

      <GaugeChart
        id="volume-gauge-chart"
        nrOfLevels={420}
        arcsLength={[0.3, 0.5, 0.2]}
        colors={["#f60707", "#f9e424", "#09b11d"]}
        percent={volumePercentage}
        arcPadding={0.02}
        textColor="transparent"
        needleColor="red"
      />
      <div className="volume-display">
        <span className="volume-value">{latestVolume}L</span>
      </div>

      <div
        className="small-graph"
        style={{ width: "100%" }}
        onClick={() => setShowLargeGraph(true)}
      >
        <LineChart
          height={120}
          series={[
            {
              data: volumeData,
              label: "Diesel Volume",
              area: true,
              showMark: true,
              color: "#2a8ef9",
              valueFormatter: (value, context) => {
                const dateIndex = context?.dataIndex;
                if (dateIndex !== undefined && actualDates[dateIndex]) {
                  const dateStr =
                    timeRange === "24hours"
                      ? formatDateTimeForTooltip(actualDates[dateIndex])
                      : formatDateForTooltip(actualDates[dateIndex]);
                  return `${value} L\n-(${dateStr})`;
                }
                return `${value} L`;
              },
              curve: "natural" as const,
            },
          ]}
          xAxis={[
            {
              scaleType: "point" as const,
              data: chartLabels,
              tickSize: 0,
              disableTicks: true,
              hideTooltip: true,
              label: "",
            },
          ]}
          yAxis={[
            {
              disableTicks: true,
              tickSize: 0,
              hideTooltip: true,
              label: "",
            },
          ]}
          disableAxisListener={true}
          margin={margin}
        />
      </div>

      {showLargeGraph && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{getChartTitle()}</h3>

            <div className="large-graph">
              <LineChart
                height={300}
                width={600}
                series={[
                  {
                    data: volumeData,
                    label: "Diesel Volume",
                    area: true,
                    showMark: true,
                    color: "#2a8ef9",
                    valueFormatter: (value, context) => {
                      const dateIndex = context?.dataIndex;
                      if (dateIndex !== undefined && actualDates[dateIndex]) {
                        const dateStr =
                          timeRange === "24hours"
                            ? formatDateTimeForTooltip(actualDates[dateIndex])
                            : formatDateForTooltip(actualDates[dateIndex]);
                        return `${value} L\n-(${dateStr})`;
                      }
                      return `${value} L`;
                    },
                    curve: "natural" as const,
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "point" as const,
                    data: chartLabels,
                    label: timeRange === "24hours" ? "Hour" : "Day",
                  },
                ]}
                yAxis={[
                  {
                    id: "volumeAxis",
                    scaleType: "linear" as const,
                    min: 0,
                    max: Math.max(...volumeData, 1) * 1.2,
                    label: "Volume (Litres)",
                  },
                ]}
                sx={{
                  [`& .${lineElementClasses.root}`]: {
                    display: "none",
                  },
                  "& .MuiLineElement-series-Volume": {
                    stroke: "#4285F4",
                    strokeWidth: 2,
                  },
                }}
                margin={largeMargin}
              />
            </div>

            {closeLargeGraph && (
              <button
                onClick={closeLargeGraph}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VolumeChart;
